import { ChevronDown, Copy, Eye, Library } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LibraryTemplate } from "@/common/types/templates";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  EmptyState,
  LIST_CARD,
  LoadErrorBanner,
  TOUCH_BUTTON,
} from "@/components/ui/redesign/page";
import { Skeleton } from "@/components/ui/skeleton";
import { useLibraryItemActions } from "@/components/ui/templates/library-item";
import { libraryCategoryLabel } from "@/lib/library-categories";
import {
  useLibraryCategories,
  useLibraryTemplates,
} from "@/lib/library-queries";
import { cn } from "@/lib/utils";

const LibraryRow = ({
  template,
}: {
  template: LibraryTemplate;
}): JSX.Element => {
  const { t } = useTranslation();
  const { view, copy, loading, dialogs } = useLibraryItemActions(template);
  return (
    <li
      className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:gap-4 sm:px-5"
      data-testid="library-row"
    >
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold leading-6">{template.name}</p>
        {template.optionalText && (
          <p className="mt-0.5 line-clamp-2 text-sm text-v3-text-3">
            {template.optionalText}
          </p>
        )}
        <p className="mt-1 text-xs font-medium text-v3-text-3">
          {t("templates.home.sections", { count: template.headers.length })}
        </p>
      </div>
      <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex">
        <Button
          className={cn(TOUCH_BUTTON, "gap-1.5")}
          type="button"
          variant="outline"
          onClick={view}
        >
          <Eye aria-hidden />
          {t("templates.viewTemplate")}
        </Button>
        <Button
          className={cn(TOUCH_BUTTON, "gap-1.5")}
          disabled={loading}
          type="button"
          onClick={copy}
        >
          <Copy aria-hidden />
          {loading ? (
            t("templates.copyTemplateDialog.copyingButton")
          ) : (
            <>
              <span className="sm:hidden">{t("templates.home.copy")}</span>
              <span className="max-sm:hidden">
                {t("templates.copyToMyTemplates")}
              </span>
            </>
          )}
        </Button>
      </div>
      {dialogs}
    </li>
  );
};

const RowsSkeleton = (): JSX.Element => (
  <div aria-hidden className="divide-y">
    {["first", "second"].map((row) => (
      <div key={row} className="space-y-2 px-4 py-4 sm:px-5">
        <Skeleton className="h-4 w-1/3" role="presentation" />
        <Skeleton className="h-3 w-2/3" role="presentation" />
      </div>
    ))}
  </div>
);

// One specialty: a card whose header opens it; its templates load on first
// open, as before.
const CategoryCard = ({
  language,
  category,
  label,
}: {
  language: string;
  category: string | null;
  label: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { templates, loading, error, refresh } = useLibraryTemplates(
    language,
    category,
    open,
  );
  return (
    <Collapsible
      className={LIST_CARD}
      data-testid="library-category"
      open={open}
      onOpenChange={setOpen}
    >
      <CollapsibleTrigger className="flex min-h-14 w-full items-center gap-3 px-4 text-left transition-colors hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-v3-brand-ring sm:px-5">
        <span className="min-w-0 flex-1 truncate text-base font-semibold">
          {label}
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            "size-5 shrink-0 text-v3-text-3 transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-v3-border data-[state=open]:animate-in data-[state=open]:fade-in-0">
        {loading ? (
          <RowsSkeleton />
        ) : error ? (
          <LoadErrorBanner
            className="m-4 sm:m-5"
            title={t("error.unknown")}
            onRetry={() => {
              void refresh();
            }}
          />
        ) : templates.length ? (
          <ul className="divide-y">
            {templates.map((template) => (
              <LibraryRow key={template.public_id} template={template} />
            ))}
          </ul>
        ) : (
          <p className="px-4 py-6 text-center text-sm text-v3-text-3 sm:px-5">
            {t("templates.library.emptyCategory")}
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Library: the same categories, order and lazy loading as the classic list,
// as cards.
export const TemplateLibrary = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { categories, language, loading, error, refresh } =
    useLibraryCategories(i18n.language);
  const label = (category: string | null): string =>
    category === null
      ? t("templates.library.noCategory")
      : category === ""
        ? t("templates.library.blankCategory")
        : libraryCategoryLabel(category, t, i18n.language);
  const uncategorized = (category: string | null): number =>
    category === null || category === "" || category === "Uncategorized"
      ? 1
      : 0;
  const sorted = [...categories].sort(
    (a, b) =>
      uncategorized(a) - uncategorized(b) || label(a).localeCompare(label(b)),
  );

  return (
    <div className="space-y-4" data-testid="template-library">
      <p className="text-sm text-v3-text-3">
        {t("templates.home.libraryIntro")}
      </p>
      {loading ? (
        <div
          aria-busy
          aria-label={t("templates.home.libraryLoading")}
          className="space-y-3"
          role="status"
        >
          {[0, 1, 2].map((index) => (
            <Skeleton
              key={index}
              className="h-14 w-full rounded-xl"
              role="presentation"
            />
          ))}
        </div>
      ) : error ? (
        <LoadErrorBanner
          title={t("error.unknown")}
          onRetry={() => {
            void refresh();
          }}
        />
      ) : sorted.length ? (
        <div className="space-y-3">
          {sorted.map((category) => (
            <CategoryCard
              key={JSON.stringify([i18n.language, language, category])}
              category={category}
              language={language}
              label={label(category)}
            />
          ))}
        </div>
      ) : (
        <EmptyState icon={Library} title={t("templates.library.empty")} />
      )}
    </div>
  );
};
